import { useCallback, useEffect, useRef, useState } from "react";
import { Galleria, GalleriaItemChangeEvent } from "primereact/galleria";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectViewingAttachments } from "../../store/selectors/attachmentSelectors";
import { setViewingAttachments } from "../../store/slices/attachmentSlice";
import { Attachment } from "../../types/attachment";
import { Image } from "primereact/image";

export const AttachmentGalleria = () => {
    const dispatch = useAppDispatch();
    const attachments = useAppSelector(selectViewingAttachments);

    const [value, setValue] = useState<(Attachment | string)[]>([""]);
    const [activeIndex, setActiveIndex] = useState(0);

    const ref = useRef<Galleria>(null);

    useEffect(() => {
        if (attachments.length && ref.current) {
            setValue(attachments);
            ref.current?.show();
        }
    }, [attachments]);

    const itemTemplate = useCallback(
        (item: Attachment | string) => {
            if (typeof item === "string") return null;

            return (
                <div className="flex flex-column align-items-center">
                    <Image
                        src={item.url}
                        alt={item.filename}
                        imageStyle={{ maxHeight: "80vh", maxWidth: "90vw", objectFit: "contain" }}
                    />
                    <div className="text-sm mt-2 text-color-secondary">
                        {item.filename}
                    </div>
                </div>
            );
        },
        []
    );

    const thumbnailTemplate = useCallback(
        (item: Attachment | string) => {
            if (typeof item === "string") return null;

            return (
                <img
                    src={item.url}
                    alt={item.filename}
                    style={{ width: "80px", height: "60px", objectFit: "cover" }}
                />
            );
        },
        []
    );

    const onItemChange = useCallback(
        (event: GalleriaItemChangeEvent) => setActiveIndex(event.index),
        []
    );

    const onHide = useCallback(() => {
        dispatch(setViewingAttachments([]));
        setValue([""]);
        setActiveIndex(0);
    }, [dispatch]);

    return (
        <Galleria
            ref={ref}
            fullScreen
            showIndicators
            showThumbnails={value.length > 1}
            activeIndex={activeIndex}
            value={value}
            showItemNavigators={value.length > 1}
            item={itemTemplate}
            thumbnail={thumbnailTemplate}
            onItemChange={onItemChange}
            onHide={onHide}
            numVisible={5}
        />
    );
};
